import React from "react";
// Popups
import PortalContainer from "@widget/popups/PortalContainer";
import Tooltip from "@widget/popups/Tooltip";
import InfoButton from "@widget/popups/InfoButton";
import DropDownButton from "@widget/popups/DropDownButton";
import Popover from "@widget/popups/Popover";
import MobileSheet from "@widget/popups/MobileSheet";
import MobileToolbar from "@widget/other/MobileToolbar";
import Modal from "@widget/popups/Modal";
import ModalWizard from "@widget/forms/wizard/ModalWizard";
import FilterPanel from "@widget/popups/FilterPanel";
import { Dropdown } from "@widget/popups/Dropdown";
// Forms
import { Wizard } from "@widget/forms/wizard/Wizard";
import { WizardStepIndicator } from "@widget/forms/DynamicForm/WizardStepIndicator";
import FormField from "@widget/forms/inputs/FormField";
import FormFeedback from "@widget/forms/inputs/FormFeedback";
import Label from "@widget/forms/inputs/Label";
import Input from "@widget/forms/inputs/Input";
import PasswordInput from "@widget/forms/inputs/PasswordInput";
import Checkbox from "@widget/forms/inputs/Checkbox";
import CheckboxList from "@widget/forms/inputs/CheckboxList";
import RadioGroup from "@widget/forms/inputs/RadioGroup";
import Select from "@widget/forms/inputs/Select";
import MultiSelect from "@widget/forms/inputs/MultiSelect";
import DynamicSelect from "@widget/forms/inputs/DynamicSelect";
import CascadingSelect from "@widget/forms/inputs/CascadingSelect";
import SearchListModal from "@widget/forms/inputs/search-modal/SearchListModal";
import AppRoleInput from "@widget/forms/inputs/AppRoleInput";
import DatePicker from "@widget/forms/inputs/DateTimePicker/DatePicker";
import DateRangePicker from "@widget/forms/inputs/DateTimePicker/DateRangePicker";
import TimePicker from "@widget/forms/inputs/DateTimePicker/TimePicker";
import TimeRangePicker from "@widget/forms/inputs/DateTimePicker/TimeRangePicker";
import DateTimePicker from "@widget/forms/inputs/DateTimePicker/DateTimePicker";
import SearchBox from "@widget/forms/inputs/SearchBox";
import CalendarComponent from "@widget/forms/inputs/DateTimePicker/CalendarComponent";
import { lazyComponent, lazyNamed } from "./lazy";
import { lazyView } from "./lazy-view";
import { DashboardSkeleton } from "@core/dashboard/DashboardSkeleton";
import { DEFAULT_BREAKPOINTS, calculateRowHeight, calculateWidgetPixelSize, pickBreakpointForWidth } from "@core/dashboard/dashboardUtils";
import { ensureExternalBundle } from "./external-bundles";
import AutoCompleteInput from "@widget/forms/inputs/AutoComplete";
import TextArea from "@widget/forms/inputs/TextArea";
import FileInput from "@widget/forms/inputs/FileInput/FileInput";
import ImagePicker, { ImagePickerModal } from "@widget/forms/inputs/ImagePicker/ImagePicker";
import MediaPicker from "@widget/forms/inputs/MediaPicker/MediaPicker";
import MediaPickerModal from "@widget/forms/inputs/MediaPicker/MediaPickerModal";
import { importImageFromUrl, ImageImportError } from "@widget/forms/inputs/MediaPicker/importImage";
import { readImageDpi, readImagePixels, runMediaCheck, runFileCheck, setImageDpi, isCroppableImage } from "@widget/forms/inputs/MediaPicker/mediaLibrary";
import UploadStatus from "@widget/forms/inputs/MediaPicker/UploadStatus";
import UploadView from "@widget/forms/inputs/MediaPicker/UploadView";
import MediaStripCard from "@widget/forms/inputs/MediaPicker/MediaStripCard";
import { useLibraryUpload, fetchMyMedia, fetchMediaFolders, fetchMediaTags, mediaItemUrl, formatFileSize, deriveMediaType } from "@widget/forms/inputs/MediaPicker/mediaLibrary";
import MediaPreview from "@widget/forms/inputs/MediaPicker/MediaPreview";
import MediaStage from "@widget/forms/inputs/MediaPicker/MediaStage";
import MediaLibraryTree, { mediaSelectionQuery, defaultMediaSelection } from "@widget/forms/inputs/MediaPicker/MediaLibraryTree";
import IconPicker from "@widget/forms/inputs/IconPicker/IconPicker";
import ToggleFilter from "@widget/forms/inputs/ToggleFilter";
import { applyFormatters, extractAndFormatValue, hasPlaceholders, parsePlaceholders } from "@widget/forms/inputs/PillInput/pill-input-utils";
import TagInput from "@widget/forms/inputs/TagInput";
import InfoCardGroupInput from "@widget/forms/inputs/InfoCardGroupInput";
import { HelpPopover } from "@widget/other/HelpPopover";
import { ConfigurationView } from "@widget/forms/ConfigurationView/ConfigurationView";
import { ConfigurationViewHeader } from "@widget/forms/ConfigurationView/ConfigurationViewHeader";
import { ConfigurationViewContent } from "@widget/forms/ConfigurationView/ConfigurationViewContent";
import { DataEntryForm, DataEntryField, DataEntrySection, SlideInForm } from "@widget/forms/DataEntryForm";
import DynamicForm from "@widget/forms/DynamicForm/DynamicForm";
// Buttons
import Button from "@widget/buttons/Button";
import AsyncButton from "@widget/buttons/AsyncButton";
import IconButton from "@widget/buttons/IconButton";
import ConfirmButton from "@widget/buttons/ConfirmButton";
import ButtonGroup from "@widget/buttons/ButtonGroup";
import ButtonComponent from "@widget/buttons/ButtonComponent";
// Lists
// DataList/DataTable/DataGrid are light (no dnd) and DataList backs the core
// Select/MultiSelect dropdowns - they stay eager
import DataList from "@widget/list/DataList";
import DataTable from "./components/deprecated/DataTable";
import DataGrid from "@widget/list/DataGrid";
import HorizontalScrollList from "@widget/list/HorizontalScrollList";
import ItemCard from "@widget/list/ItemCard";
import ListItemCard from "@widget/list/ListItemCard";
import ObjectCard from "@widget/list/ObjectCard/ObjectCard";
import TabSectionHeader from "@widget/list/Tabs/TabSectionHeader";
import ItemListCard from "@widget/list/ItemListCard";
import InfoCard from "@widget/list/InfoCard";
import UserInfoCard from "@widget/list/UserInfoCard";
import InfoCardGroup from "@widget/list/InfoCardGroup";
import TabComponent from "@widget/list/Tabs/TabComponent";
import PaginationComponent from "@widget/list/Pagination/PaginationComponent";
import { generateTreeNodesFromFlatList, flattenTreeNodesToOriginalFlatList, findTreeNodeById, findTreeNodeByIdSync, findTreeNodeByPath, processTreeNode, processAllTreeNodes } from "@widget/list/TreeView/treeview-utils";
import { FilterView, isSimpleFilter, getFilterCount, cleanFilters, hasFilters } from "@widget/list/Tables/object-search/FilterView";
import ActionsListComponent from "@widget/list/Tables/Common/ActionListComponent";
// Loaders
import { Loading, LoadingSpinner, DefaultLoader, BarChartLoader, DonutChartLoader, GaugeLoader, HeatmapChartLoader, MapTileLoader, SkeletonLoader, LoadingFeedback, ProgressBar, ViewSkeleton, DetailsPanelSkeleton, CodeEditorLoader } from "@widget/loaders";
// Other widgets
import TitleBar from "@widget/other/TitleBar";
import LinkWidgetContainer from "@widget/other/LinkWidgetContainer";
import LinkButtonWidget from "@widget/other/LinkButtonWidget";
import SampleDataLabel from "@widget/other/SampleDataLabel";
import ProfileImage from "@widget/other/ProfileImage";
import WidgetWrapper from "@widget/other/WidgetWrapper";
import { Collapse } from "@widget/other/Collapse";
import Chip from "@widget/other/Chip";
import ProgressList from "@widget/other/ProgressList";
import ListDropdown from "@widget/other/ListDropdown";
import DropdownIndicator from "@widget/other/DropdownIndicator";
import { Breadcrumb } from "@widget/other/Breadcrum";
import { AnalyticsCard } from "@widget/other/AnalyticsCard";
import { SlideInPanel } from "@widget/other/SlideInPanel";
// Map
import MapMarker from "@widget/map/MapMarker";
// Icons
import Icon from "@icon/Icon";
import { FontAwesomeIconWrapper } from "@icon/fontawesome/FontAwesomeIconWrapper";
import { useIsFAIconReady } from "@icon/fontawesome/useIsFAIconReady";
import { PHIconWrapper } from "@icon/phosphor/PHIconWrapper";
import { useIsPHIconReady } from "@icon/phosphor/useIsPHIconReady";
import { parseIcon, hasIcon, isPhosphorIcon, isImageUrl, getPhosphorIcon } from "@icon/utils";
// Hooks
import useToast from "@notifications/Toast/useToast";
import useAlert from "@notifications/Alerts/useAlerts";
import useLoadingFeedback from "@notifications/LoadingFeedback/useLoadingFeedback";
import useDebounce from "@hooks/useDebounce";
import useFileUpload, { uploadFile, getUploadSizeLimitMB } from "@hooks/useFileUpload";
import useFields from "@hooks/useFields";
import useResizeEffect from "@hooks/useResizeEffect";
import useRemToPx, { remToPx } from "@hooks/useRemToPx";
import useIsMobileLayout, { MOBILE_MEDIA_QUERY, isMobileLayout, useViewportFlag } from "@hooks/useIsMobileLayout";
import useMobileLayer, { useHasMobileShell, useHasMobileCloseBar } from "@core/layout/MobileLayer";
import useRowGestures from "@hooks/useRowGestures";
import usePressGesture from "@hooks/touch/usePressGesture";
import ActionsSheet from "@widget/popups/ActionsSheet";
import SwipeActions from "@widget/other/SwipeActions";
import CameraCapture from "@widget/other/CameraCapture/CameraCapture";
import ImageCropper from "@widget/other/ImageCropper/ImageCropper";
import useDragSensors from "@hooks/touch/useDragSensors";
import { isFakeMouse, useIsTouchScreen } from "@hooks/touch/touchRules";
// Touch: press and hold an icon-only control for its label, on every page
import "@hooks/touch/touchTips";
import ButtonListContext from "@widget/buttons/ButtonListContext";
import useUpdateWidgetProps from "@hooks/useUpdateWidgetProps";
import useEffectWithPolling from "@hooks/useEffectWithPolling";
import useHasChanged from "@hooks/useHasChanged";
import useCounter from "@hooks/useCounter";
import useWidgets from "@hooks/useWidgets";
import { useFormatters } from "@hooks/useFormatters";
import { useExportData } from "@hooks/useExportData";
import { useSearchParams, getSearchParamsFromURL, useRouterContext } from "@hooks/useRouteHandlers";
// Realtime
import { useMessageBus, publishMessage } from "@realtime/messagebus/useMessageBus";
import { connectHub } from "@realtime/hub/hubConnection";
import { eventDispatcher, useEventSubscriber, useMultiEventSubscriber } from "@realtime/eventbus/useEventBus";
import { useDashboardFilters } from "@core/dashboard/DashboardFiltersContext";
// Core
import UserProfile from "@core/layout/components/UserProfile";
import { useUXPContext } from "@core/context/useUXPContext";
import { executeConfig, useExecuteRequest, useExecuteRequestCallback } from "@core/context/useExecuteRequest";
import { useLogout } from "@core/services/useLogout";
import { isFeatureAvailable, isMissingFeatureError, useFeatureAvailable, executeIfAvailable } from "@core/services/capabilities";
import { getDefaultGlobeLocations } from "@core/services/auth_services";
import { SafeLink } from "@core/route-context/RouteContext";
import { usePluggableView } from "@core/pages/pluggable-view/usePluggableView";
import PluggableViewConfig from "@core/pages/page-editor/PluggableViewConfig";
import { useTabInjection } from "@core/pages/injected-tabs/useTabInjection";
import { registerTabInjection } from "@core/pages/injected-tabs/tabInjectionRegistry";
import ComponentRenderer from "@helper/ComponentRenderer/ComponentRenderer";
import { registerFloatingActionSection, unregisterFloatingActionSection } from "@core/floating-actions/floatingActionsRegistry";
// Helpers
import useDeleteAction from "@helper/DeleteAction/useDeleteAction";
import DeleteAction from "@helper/DeleteAction/DeleteAction";
import NotificationBlock from "@helper/NotificationBlock";
import ErrorState from "@helper/ErrorState";
import { DesktopOnly, NotAuthorized, NotFound } from "@core/layout/components/ErrorPages";
import { useAddOns } from "@helper/AddOn/useAddOns";
import { BuyOnSpaceworxButton, SpaceworxDescriptionTag } from "@helper/BuyOnSpaceworx";
import Transition from "@helper/transition/Transition";
// Localisation
import LocalizationForm from "@localisation/LocalisationForm";
import LocalizationFormModal from "@localisation/LocalisationFormModal";
import { getRegisterMessages } from "@localisation/localizationUtils";
import { saveLocalizations } from "@core/services/services";
// Theme
import ThemeChanger from "@theme/ThemeChanger";
import { ThemeType } from '@types';
import { defaultThemes, defaultLightTheme, defaultDarkTheme, defaultGlassDarkTheme, defaultGlassLightTheme, getColorGroups, getDefaultThemes, getDefaultThemeNames } from "@theme/themeUtils";
// Utilities
import { copyToClipboard, debounce, generateScriptPathFromModuleId, lookupComponent, normalizeModuleScriptUrl, generateUUID, getFontAwesomeIcon, getModuleIdFromScriptPath, getModuleIdFromWidget, getRelativeDateLabel, getUrlFriendlyString, hasValue, isFontawesomeIcon, isOfflineDeployment, isRelativeDate, loadAllScriptsByModuleId, loadScriptFile, loadScriptFileAsync, normalizeComponentId, parseRelativeDate, simpleHash, textSearch, toBool, toDate, toJSON, toNum, toPlural, toSingular, toStr, trimLeadingSlash, trimSlash, trimTrailingSlash, resolveResourceUrl } from "@utils";
import "@core/dashboard/builtinWidgets"; // registers UXP Core built-in widgets (Widget Panel, Diagnostic)
// ── Lazy chunks ────────────────────────────────────────────────────────────
// Heavy components load on first render instead of shipping in main.js.
// One webpackChunkName per feature group = one network fetch per feature.
// The exports below still exist synchronously on window.UXPComponents, so
// app bundles that dereference them at eval time are unaffected; refs are
// forwarded (CodeEditor/MarkdownEditor/PillInput imperative handles work).
// map: leaflet + react-leaflet + leaflet.heat - biggest single dependency
const MapComponent = lazyComponent(() => import(/* webpackChunkName: "map" */ "@widget/map/MapComponent"), React.createElement(MapTileLoader));
// charts: moves the window.Recharts dereference out of main.js eval, which
// lets the login page drop the recharts script entirely
const PieChartComponent = lazyComponent(() => import(/* webpackChunkName: "charts" */ "@widget/charts/PieChart"), React.createElement(DonutChartLoader));
const TrendChartComponent = lazyComponent(() => import(/* webpackChunkName: "charts" */ "@widget/charts/TrendChart"), React.createElement(BarChartLoader));
const RadialGauge = lazyComponent(() => import(/* webpackChunkName: "charts" */ "@widget/charts/RadialGauge"), React.createElement(GaugeLoader));
// editors: marked + jsonpath-plus. The monaco/codemirror runtimes load on
// demand BEFORE the module evaluates (PillInput resolves its WidgetType base
// at eval time), so the host page no longer ships them as eager head scripts.
const CodeEditor = lazyComponent(() => ensureExternalBundle("monaco")
    .then(() => import(/* webpackChunkName: "editors" */ "@widget/forms/inputs/CodeEditor/CodeEditor")), React.createElement(CodeEditorLoader));
const MarkdownEditor = lazyComponent(() => ensureExternalBundle("monaco")
    .then(() => import(/* webpackChunkName: "editors" */ "@widget/forms/inputs/MarkdownEditor/MarkdownEditor")), React.createElement(CodeEditorLoader));
const MarkdownPreview = lazyComponent(() => import(/* webpackChunkName: "editors" */ "@widget/forms/inputs/MarkdownPreview/MarkdownPreview"));
const PillInput = lazyComponent(() => ensureExternalBundle("codemirror")
    .then(() => import(/* webpackChunkName: "editors" */ "@widget/forms/inputs/PillInput/PillInput")));
const Pill = lazyNamed(() => ensureExternalBundle("codemirror")
    .then(() => import(/* webpackChunkName: "editors" */ "@widget/forms/inputs/PillInput/PillInput")), "Pill");
const PillDropdown = lazyNamed(() => ensureExternalBundle("codemirror")
    .then(() => import(/* webpackChunkName: "editors" */ "@widget/forms/inputs/PillInput/PillInput")), "PillDropdown");
// datagrid: @dnd-kit + the table/treeview/object-search suites
const SortableList = lazyNamed(() => import(/* webpackChunkName: "datagrid" */ "@widget/list/SortableList"), "SortableList");
const SimpleConfigurationTable = lazyNamed(() => import(/* webpackChunkName: "datagrid" */ "@widget/forms/SimpleConfigurationTable/SimpleConfigurationTable"), "SimpleConfigurationTable");
const TableComponent = lazyComponent(() => import(/* webpackChunkName: "datagrid" */ "@widget/list/Tables/TableComponent"));
const CRUDComponent = lazyComponent(() => import(/* webpackChunkName: "datagrid" */ "./components/deprecated/CRUDComponent"));
const TreeView = lazyComponent(() => import(/* webpackChunkName: "datagrid" */ "@widget/list/TreeView/TreeView"));
const TreeViewCompare = lazyComponent(() => import(/* webpackChunkName: "datagrid" */ "@widget/list/TreeView/TreeViewCompare"));
const TreeViewSelectInput = lazyComponent(() => import(/* webpackChunkName: "datagrid" */ "@widget/list/TreeView/TreeViewSelect"));
const ObjectSearchComponent = lazyNamed(() => import(/* webpackChunkName: "datagrid" */ "@widget/list/Tables/object-search/ObjectSearchComponent"), "ObjectSearchComponent");
const ObjectDetailsPanel = lazyNamed(() => import(/* webpackChunkName: "datagrid" */ "@widget/list/Tables/object-search/ObjectDetailsPanel"), "ObjectDetailsPanel");
const ObjectInfoCard = lazyNamed(() => import(/* webpackChunkName: "datagrid" */ "@widget/list/Tables/object-search/ObjectInfoCardComponent"), "ObjectInfoCard");
const ObjectDetailsPanelHeader = lazyNamed(() => import(/* webpackChunkName: "datagrid" */ "@widget/list/Tables/object-search/ObjectDetailsPanelHeader"), "ObjectDetailsPanelHeader");
// dashboard: react-grid-layout + react-draggable + the widget tooling
const WidgetDrawer = lazyComponent(() => import(/* webpackChunkName: "dashboard" */ "@core/dashboard/widget-drawer/WidgetDrawer"));
const EmbeddedDashboard = lazyNamed(() => import(/* webpackChunkName: "dashboard" */ "@core/dashboard/embedded-dashboard/EmbeddedDashboard"), "EmbeddedDashboard");
const Dashboard = lazyComponent(() => import(/* webpackChunkName: "dashboard" */ "@core/dashboard/Dashboard"));
const FloatingActions = lazyComponent(() => import(/* webpackChunkName: "dashboard" */ "@core/floating-actions/FloatingActions"));
const DashboardConfigPanel = lazyComponent(() => import(/* webpackChunkName: "dashboard" */ "@core/dashboard/configurations/DashboardConfigPanel"));
// color: @uiw/react-color-sketch
const ColorPicker = lazyComponent(() => import(/* webpackChunkName: "color" */ "@widget/forms/inputs/ColorPicker/ColorPicker"));
const ColorPallet = lazyComponent(() => import(/* webpackChunkName: "color" */ "@widget/forms/inputs/ColorPicker/ColorPallet"));
// qr: react-qr-code
const QRCodeComponent = lazyNamed(() => import(/* webpackChunkName: "qr" */ "@widget/qr-code/QRCode"), "QRCodeComponent");
// viewers: pdfjs-dist. Its worker is a separate asset, fetched only when a document opens.
const DocumentViewer = lazyComponent(() => import(/* webpackChunkName: "viewers" */ "@widget/other/DocumentViewer/DocumentViewer"), React.createElement(LoadingSpinner));
// sheets: SheetJS (xlsx), the same chunk the Excel export loads
const SheetViewer = lazyComponent(() => import(/* webpackChunkName: "sheet-viewer" */ "@widget/other/SheetViewer/SheetViewer"), React.createElement(LoadingSpinner));
// chat: the message list and box. The message box is a MarkdownEditor, so it loads the editor runtime first.
const ChatComponent = lazyComponent(() => ensureExternalBundle("monaco")
    .then(() => import(/* webpackChunkName: "chat" */ "@widget/other/Chat/ChatComponent")), React.createElement(LoadingSpinner));
const ChatInput = lazyComponent(() => ensureExternalBundle("monaco")
    .then(() => import(/* webpackChunkName: "chat" */ "@widget/other/Chat/ChatInput")));
window.UXPComponents = {
    // Popups
    PortalContainer,
    Tooltip,
    InfoButton,
    DropDownButton,
    Popover,
    MobileSheet,
    MobileToolbar,
    Modal,
    ModalWizard,
    FilterPanel,
    Dropdown,
    // Forms
    Wizard,
    WizardStepIndicator,
    FormField,
    FormFeedback,
    Label,
    Input,
    PasswordInput,
    Checkbox,
    CheckboxList,
    RadioGroup,
    Select,
    MultiSelect,
    DynamicSelect,
    CascadingSelect,
    SearchListModal,
    AppRoleInput,
    DatePicker,
    DateRangePicker,
    TimePicker,
    TimeRangePicker,
    DateTimePicker,
    SearchBox,
    CalendarComponent,
    ColorPicker,
    ColorPallet,
    AutoCompleteInput,
    TextArea,
    FileInput,
    ImagePicker,
    ImagePickerModal,
    MediaPicker,
    MediaPickerModal,
    importImageFromUrl,
    readImageDpi,
    readImagePixels,
    runMediaCheck,
    runFileCheck,
    ImageImportError,
    UploadStatus,
    UploadView,
    MediaStripCard,
    useLibraryUpload,
    fetchMyMedia,
    fetchMediaFolders,
    fetchMediaTags,
    mediaItemUrl,
    formatFileSize,
    deriveMediaType,
    MediaPreview,
    MediaStage,
    MediaLibraryTree,
    mediaSelectionQuery,
    defaultMediaSelection,
    IconPicker,
    ToggleFilter,
    PillInput,
    Pill,
    PillDropdown,
    CodeEditor,
    MarkdownPreview,
    MarkdownEditor,
    TagInput,
    parsePlaceholders,
    hasPlaceholders,
    extractAndFormatValue,
    applyFormatters,
    InfoCardGroupInput,
    HelpPopover,
    ConfigurationView,
    ConfigurationViewHeader,
    ConfigurationViewContent,
    SimpleConfigurationTable,
    DataEntryForm,
    DataEntryField,
    DataEntrySection,
    SlideInForm,
    DynamicForm,
    // Buttons
    Button,
    AsyncButton,
    IconButton,
    ConfirmButton,
    LinkButtonWidget,
    ButtonGroup,
    ButtonComponent,
    // Lists
    DataList,
    DataTable,
    DataGrid,
    SortableList,
    HorizontalScrollList,
    ItemCard,
    ListItemCard,
    ObjectCard,
    TabSectionHeader,
    ItemListCard,
    InfoCard,
    UserInfoCard,
    InfoCardGroup,
    TabComponent,
    TableComponent,
    PaginationComponent,
    CRUDComponent,
    TreeView,
    TreeViewCompare,
    TreeViewSelectInput,
    generateTreeNodesFromFlatList,
    flattenTreeNodesToOriginalFlatList,
    findTreeNodeById,
    findTreeNodeByIdSync,
    findTreeNodeByPath,
    processTreeNode,
    processAllTreeNodes,
    ObjectSearchComponent,
    ObjectDetailsPanel,
    ObjectInfoCard,
    ObjectDetailsPanelHeader,
    FilterView,
    isSimpleFilter,
    getFilterCount,
    cleanFilters,
    hasFilters,
    ActionsListComponent,
    // Charts
    PieChartComponent,
    TrendChartComponent,
    RadialGauge,
    // Map
    MapComponent,
    MapMarker,
    // Loaders
    Loading,
    LoadingSpinner,
    DefaultLoader,
    BarChartLoader,
    DonutChartLoader,
    GaugeLoader,
    HeatmapChartLoader,
    MapTileLoader,
    SkeletonLoader,
    LoadingFeedback,
    ProgressBar,
    CodeEditorLoader,
    // Lazy view helper + page skeletons
    lazyView,
    ViewSkeleton,
    DashboardSkeleton,
    DetailsPanelSkeleton,
    // Other widgets
    TitleBar,
    LinkWidgetContainer,
    SampleDataLabel,
    ProfileImage,
    WidgetWrapper,
    Collapse,
    Chip,
    ProgressList,
    ListDropdown,
    DropdownIndicator,
    Breadcrumb,
    AnalyticsCard,
    SlideInPanel,
    QRCodeComponent,
    DocumentViewer,
    SheetViewer,
    ChatComponent,
    ChatInput,
    // Icons
    Icon,
    FontAwesomeIconWrapper,
    useIsFAIconReady,
    PHIconWrapper,
    useIsPHIconReady,
    parseIcon,
    hasIcon,
    isPhosphorIcon,
    isImageUrl,
    getPhosphorIcon,
    isFontawesomeIcon,
    getFontAwesomeIcon,
    // Hooks
    useToast,
    useAlert,
    useLoadingFeedback,
    useDeleteAction,
    DeleteAction,
    useDebounce,
    useFileUpload,
    uploadFile,
    getUploadSizeLimitMB,
    useFields,
    useResizeEffect,
    useRemToPx,
    remToPx,
    useIsMobileLayout,
    MOBILE_MEDIA_QUERY,
    isMobileLayout,
    useViewportFlag,
    useMobileLayer,
    useHasMobileShell,
    useHasMobileCloseBar,
    useRowGestures,
    usePressGesture,
    ActionsSheet,
    SwipeActions,
    CameraCapture,
    ImageCropper,
    setImageDpi,
    isCroppableImage,
    useDragSensors,
    isFakeMouse,
    useIsTouchScreen,
    ButtonListContext,
    useUpdateWidgetProps,
    useEffectWithPolling,
    useEventSubscriber,
    useMultiEventSubscriber,
    eventDispatcher,
    useDashboardFilters,
    useHasChanged,
    useAddOns,
    useCounter,
    useWidgets,
    useFormatters,
    useUXPContext,
    useExecuteRequest,
    useExecuteRequestCallback,
    executeConfig,
    useLogout,
    isFeatureAvailable,
    isMissingFeatureError,
    useFeatureAvailable,
    executeIfAvailable,
    useExportData,
    useMessageBus,
    publishMessage,
    connectHub,
    useSearchParams,
    getSearchParamsFromURL,
    useRouterContext,
    usePluggableView,
    PluggableViewConfig,
    useTabInjection,
    registerTabInjection,
    // Core
    UserProfile,
    SafeLink,
    ComponentRenderer,
    WidgetDrawer,
    EmbeddedDashboard,
    Dashboard,
    FloatingActions,
    registerFloatingActionSection,
    unregisterFloatingActionSection,
    DashboardConfigPanel,
    // Helpers
    NotificationBlock,
    ErrorState,
    DesktopOnly,
    NotAuthorized,
    NotFound,
    BuyOnSpaceworxButton,
    SpaceworxDescriptionTag,
    Transition,
    // Localisation
    LocalizationForm,
    LocalizationFormModal,
    getRegisterMessages,
    saveLocalizations,
    // Theme
    ThemeChanger,
    ThemeType,
    defaultThemes,
    defaultLightTheme,
    defaultDarkTheme,
    defaultGlassDarkTheme,
    defaultGlassLightTheme,
    getColorGroups,
    getDefaultThemes,
    getDefaultThemeNames,
    // Utilities
    hasValue,
    toJSON,
    toStr,
    toNum,
    toBool,
    toDate,
    toSingular,
    toPlural,
    textSearch,
    trimSlash,
    trimTrailingSlash,
    trimLeadingSlash,
    getUrlFriendlyString,
    parseRelativeDate,
    isRelativeDate,
    getRelativeDateLabel,
    getModuleIdFromWidget,
    getModuleIdFromScriptPath,
    generateScriptPathFromModuleId,
    normalizeModuleScriptUrl,
    loadScriptFile,
    loadScriptFileAsync,
    loadAllScriptsByModuleId,
    simpleHash,
    generateUUID,
    copyToClipboard,
    normalizeComponentId,
    lookupComponent,
    debounce,
    isOfflineDeployment,
    resolveResourceUrl,
    getDefaultGlobeLocations,
    DEFAULT_BREAKPOINTS,
    calculateRowHeight,
    calculateWidgetPixelSize,
    pickBreakpointForWidth,
};
